import { FiAlertCircle, FiActivity, FiClock, FiRefreshCw, FiCalendar } from 'react-icons/fi';
import { usePublishAccess } from '../../contexts/PublishAccessContext';
import SchedulerPlanningProgressBar from './SchedulerPlanningProgressBar';

export default function SchedulerCardStatusSection({
  schedulerApiUrl,
  connectionError,
  setConnectionError,
  fetchSchedulerStatus,
  fetchCurrentConfig,
  schedulerStatus,
  apiError,
  handleShowConfigForm,
  isStarting,
  isStopping,
  handleStopScheduler,
  formatElapsedTime,
  statusMessage,
  fetchModalConfig,
  setModalConfig,
  setShowDetailsModal,
  embedded = false,
  // Render props for merged layout (only used when embedded)
  headerTitleExtra = null,
  headerActionsExtra = null,
  statsExtra = null,
  showViewDetails = false,
}) {
  const { adminModeUnlocked } = usePublishAccess();
  const statusColor = schedulerStatus?.status === 'running'
    ? 'text-emerald-600'
    : schedulerStatus?.status === 'waiting'
      ? 'text-blue-600'
      : 'text-amber-600';

  const statusDot = schedulerStatus?.status === 'running'
    ? 'bg-emerald-500'
    : schedulerStatus?.status === 'waiting'
      ? 'bg-blue-500'
      : 'bg-amber-500';

  const statusLabel = schedulerStatus?.status === 'running'
    ? 'Running'
    : schedulerStatus?.status === 'waiting'
      ? 'Waiting'
      : schedulerStatus?.status === 'stopped'
        ? 'Stopped'
        : schedulerStatus?.status ?? '—';

  const hasStatsExtra = !!statsExtra;

  const statsGridClass = hasStatsExtra
    ? (adminModeUnlocked
      ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-9'
      : 'grid-cols-2 md:grid-cols-4 lg:grid-cols-8')
    : (adminModeUnlocked
      ? 'grid-cols-2 md:grid-cols-4'
      : 'grid-cols-2 md:grid-cols-3');

  const content = (
    <>
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-950/5">
        {/* LEFT: Scheduler identity + injected trip title */}
        <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {embedded ? (
              <div className="bg-dashboard-stats-primary-dark rounded-lg p-1.5 flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-dashboard-stats-primary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
                </svg>
              </div>
            ) : (
              <svg className="w-4 h-4 text-main-primary flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
              </svg>
            )}
            <h2 className={`text-[11px] font-semibold leading-none mb-0 whitespace-nowrap ${embedded ? 'text-dashboard-stats-primary' : 'text-sm text-main-primary'}`}>
              Scheduler
            </h2>
          </div>
          {headerTitleExtra && (
            <>
              <div className="w-px h-4 bg-dashboard-stats-primary/25 flex-shrink-0" />
              {headerTitleExtra}
            </>
          )}
        </div>

        {/* RIGHT: All action buttons */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {showViewDetails && (
            <button
              onClick={async () => {
                setModalConfig(null);
                await fetchModalConfig();
                setShowDetailsModal(true);
              }}
              className={`text-xs font-medium transition-colors ${embedded ? 'text-dashboard-stats-primary hover:text-dashboard-stats-primary/70' : 'text-main-primary hover:text-main-primary/80'}`}
            >
              View Details
            </button>
          )}
          {schedulerStatus && !connectionError && (
            !schedulerStatus.is_running ? (
              <button onClick={handleShowConfigForm} className="btn-primary !py-1 !px-3 !text-xs" disabled={isStarting}>
                {isStarting ? 'Starting...' : 'Start Scheduler'}
              </button>
            ) : (
              <button onClick={handleStopScheduler} className="btn-danger !py-1 !px-3 !text-xs" disabled={isStopping}>
                {isStopping ? 'Stopping...' : 'Stop Scheduler'}
              </button>
            )
          )}
          {headerActionsExtra}
        </div>
      </div>

      {connectionError ? (
        <div className="px-3 py-3 text-center">
          <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-2">
            <FiAlertCircle className="text-red-500 text-sm" />
          </div>
          <h3 className="text-xs font-semibold text-gray-900 mb-1">Connection Error</h3>
          <p className="text-[11px] text-gray-500 mb-2">{connectionError}</p>
          <div className="mb-2">
            <p className="text-[11px] text-gray-400 mb-1">Make sure the scheduler API is running:</p>
            <code className="block bg-gray-50 px-2 py-1.5 rounded text-[11px] font-mono text-gray-600">
              cd scheduler_api && python main.py
            </code>
            <p className="text-[11px] text-gray-400 mt-1">API: <strong>{schedulerApiUrl}</strong></p>
          </div>
          <button
            onClick={() => { setConnectionError(''); fetchSchedulerStatus(); fetchCurrentConfig(); }}
            className="btn-primary !py-1 !px-3 !text-xs"
          >
            Try Again
          </button>
        </div>
      ) : schedulerStatus ? (
        <div className="px-2 py-2">
          {/* Metrics grid — expands to 8 cols when trip stats are injected */}
          <div className={`grid gap-2 ${statsGridClass}`}>
            <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
              <dl className="flex items-center h-full">
                <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                  <FiActivity className="text-dashboard-stats-primary h-3.5 w-3.5" />
                </dt>
                <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                  <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1">Status</div>
                  <div className={`flex items-center gap-1 truncate text-xs font-bold tabular-nums ${statusColor}`}>
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDot} ${schedulerStatus?.is_running ? 'animate-pulse' : ''}`} />
                    {statusLabel}
                  </div>
                </dd>
              </dl>
            </div>

            <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
              <dl className="flex items-center h-full">
                <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                  <FiClock className="text-dashboard-stats-primary h-3.5 w-3.5" />
                </dt>
                <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                  <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1">Elapsed</div>
                  <div className="text-dashboard-stats-primary truncate text-xs font-bold font-mono tabular-nums">
                    {formatElapsedTime(schedulerStatus.elapsed_time)}
                  </div>
                </dd>
              </dl>
            </div>

            <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
              <dl className="flex items-center h-full">
                <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                  <FiRefreshCw className="text-dashboard-stats-primary h-3.5 w-3.5" />
                </dt>
                <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                  <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1">Runs</div>
                  <div className="text-dashboard-stats-primary truncate text-xs font-bold tabular-nums">
                    {schedulerStatus.run_count}
                  </div>
                </dd>
              </dl>
            </div>

            {adminModeUnlocked && (
            <div className="bg-dashboard-stats-primary-light cursor-pointer rounded-xl p-2.5 transition-transform hover:scale-[1.02]">
              <dl className="flex items-center h-full">
                <dt className="bg-dashboard-stats-primary-dark rounded-lg p-2 flex-shrink-0">
                  <FiCalendar className="text-dashboard-stats-primary h-3.5 w-3.5" />
                </dt>
                <dd className="ml-2 flex flex-1 items-center justify-between min-w-0">
                  <div className="text-dashboard-stats-info truncate text-[10px] font-medium mr-1">Interval</div>
                  <div className="text-dashboard-stats-primary truncate text-xs font-bold tabular-nums">
                    {schedulerStatus.interval_minutes} min
                  </div>
                </dd>
              </dl>
            </div>
            )}

            {statsExtra}
          </div>

          {/* API Error */}
          {apiError && (
            <div className="mt-2 px-2 py-2 bg-red-50 border border-red-100 rounded-md flex items-start gap-2">
              <FiAlertCircle className="text-red-500 text-xs mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-red-800 mb-0.5">API Configuration Error</p>
                <p className="text-[11px] text-red-600 mb-1">{apiError.message}</p>
                <button onClick={handleShowConfigForm} className="text-[11px] font-medium text-red-700 hover:text-red-800 underline">
                  Fix Configuration
                </button>
              </div>
            </div>
          )}

          {/* Status message */}
          {statusMessage && (
            <div className="mt-2 px-2 py-1.5 bg-emerald-50 border border-emerald-100 rounded-md">
              <p className="text-[11px] text-emerald-700">{statusMessage}</p>
            </div>
          )}

          <SchedulerPlanningProgressBar runProgress={schedulerStatus?.run_progress} />
        </div>
      ) : (
        <div className="px-3 py-3 text-center">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-main-primary/20 border-t-main-primary mx-auto mb-1.5" />
          <p className="text-[11px] text-gray-400">Loading scheduler status...</p>
        </div>
      )}
    </>
  );

  if (embedded) return content;

  return (
    <div className="bg-white rounded-lg border border-gray-100 shadow-sm">
      {content}
    </div>
  );
}
